import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Video, ExternalLink, X, Calendar, Monitor, CheckCircle2, Lock, Copy, Check, Plus, Shield, MapPin, Key, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import axios from "axios";
import { verifyPasscode } from "@/utils/adminAuth";
import { staffLogin } from "@/utils/staffAuth";
import { errMsg } from "@/utils/errMsg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KARINE_IMAGE = "https://customer-assets.emergentagent.com/job_9446e739-d015-4568-b85b-ec15a12a3500/artifacts/15duxq66_11d9539a-ce47-496f-a9c4-5cffeefaea67.jpg";

const locations = [
  // Jul 9 2026 — swapped from stale external Square URLs (a broken
  // tinyurl + two dead subdomains) that were redirecting clicks straight
  // to Square's own sales/marketing page. Now every "Book" tap navigates
  // to our own /book flow, prefilled with the location + booking-type
  // hint. Reliable because it uses the SAME Square API pipeline the
  // rest of the site relies on (fresh catalog, live availability,
  // AI concierge context, etc.) — no external redirect surface.
  { name: "Carleton Place", locationId: "1" },
  { name: "Kanata",         locationId: "2" },
  { name: "Ottawa",         locationId: "3" },
];

const VirtualConsultationModal = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const isFr = language === "fr";
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("book");

  // Join state
  const [roomCode, setRoomCode] = useState("");
  const [joinError, setJoinError] = useState("");
  const [isJoining, setIsJoining] = useState(false);
  const [showPostConsultation, setShowPostConsultation] = useState(false);
  
  // Book Treatment state
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedTreatmentCategory, setSelectedTreatmentCategory] = useState(null);
  
  const treatmentCategories = [
    { id: 'botox', label: isFr ? 'Botox' : 'Botox', icon: '💉' },
    { id: 'fillers', label: isFr ? 'Fillers' : 'Fillers', icon: '✨' },
    { id: 'skincare', label: isFr ? 'Soins de la Peau' : 'Skincare', icon: '🌟' },
    { id: 'consultation', label: isFr ? 'Consultation' : 'Consultation', icon: '👩‍⚕️' },
  ];

  // Generate code state
  const [genPasscode, setGenPasscode] = useState("");
  const [genAuthed, setGenAuthed] = useState(false);
  const [genCode, setGenCode] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Admin tab state
  const [adminCode, setAdminCode] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminBusy, setAdminBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setRoomCode("");
    setJoinError("");
    setShowPostConsultation(false);
    setTimeout(onClose, 200);
  };

  const handleJoinRoom = async () => {
    if (!roomCode.trim()) {
      setJoinError(isFr ? "Veuillez entrer un code de salle" : "Please enter a room code");
      return;
    }
    setIsJoining(true);
    setJoinError("");
    const newWindow = window.open("about:blank", "_blank");
    try {
      const response = await axios.post(`${API}/video/join-room`, { room_code: roomCode.trim() });
      if (response.data.success && response.data.room_url) {
        if (newWindow) newWindow.location.href = response.data.room_url;
        else window.location.href = response.data.room_url;
        // Switch to Book Treatment tab so they can easily book after the call
        setActiveTab("book-treatment");
        setIsJoining(false);
        setRoomCode("");
      } else {
        if (newWindow) newWindow.close();
        setJoinError(response.data.message || (isFr ? "Salle non trouvée" : "Room not found"));
        setIsJoining(false);
      }
    } catch {
      if (newWindow) newWindow.close();
      setJoinError(isFr ? "Erreur de connexion. Veuillez réessayer." : "Connection error. Please try again.");
    } finally {
      setIsJoining(false);
    }
  };

  const handleGenAuth = async () => {
    const isValid = await verifyPasscode(genPasscode);
    if (isValid) {
      setGenAuthed(true);
    } else {
      toast.error("Wrong passcode");
    }
  };

  // Email + password sign-in for the Admin/Staff tab.
  // Karine → /admin/hub. Jenny (or any injector) → /staff. 12h session via cookie.
  const doStaffLogin = async () => {
    if (!adminEmail || !adminPassword) return;
    setAdminBusy(true);
    try {
      const u = await staffLogin(adminEmail.trim().toLowerCase(), adminPassword);
      toast.success(`${isFr ? "Bienvenue" : "Welcome"}, ${u.name?.split(" ")[0] || ""}`);
      handleClose();
      navigate(u.role === "owner" ? "/admin/hub" : "/staff");
    } catch (err) {
      const detail = errMsg(err, isFr ? "Connexion échouée" : "Sign-in failed");
      toast.error(detail);
    } finally {
      setAdminBusy(false);
    }
  };

  const [genHostUrl, setGenHostUrl] = useState(null);

  const handleGenerateCode = async () => {
    setGenLoading(true);
    setCopied(false);
    try {
      const res = await axios.post(`${API}/video/create-room`, {});
      if (res.data.success) {
        setGenCode(res.data.room_name);
        setGenHostUrl(res.data.room_url);
        toast.success(`Zoom meeting created! Code: ${res.data.room_name}`);
      }
    } catch {
      toast.error("Failed to create Zoom meeting");
    }
    setGenLoading(false);
  };

  const startZoomCall = () => {
    if (genHostUrl) {
      window.open(genHostUrl, "_blank");
    }
  };

  const copyCode = () => {
    if (genCode) {
      navigator.clipboard.writeText(genCode);
      setCopied(true);
      toast.success("Code copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-[200] flex items-center justify-center p-4 transition-opacity duration-200 ${isVisible ? "opacity-100" : "opacity-0"}`}
      data-testid="virtual-consultation-modal"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />

      <div className={`relative bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden transform transition-all duration-200 ${isVisible ? "scale-100" : "scale-95"}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Video className="w-6 h-6 text-amber-400" />
            </div>
            <h2 className="text-2xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              {isFr ? "Consultation" : "Virtual"}{" "}
              <span className="italic text-amber-400">{isFr ? "Virtuelle" : "Consultation"}</span>
            </h2>
          </div>
          <button onClick={handleClose} className="p-3 min-w-[44px] min-h-[44px] text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-full transition-colors flex items-center justify-center" data-testid="close-virtual-modal">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs - Scrollable on mobile */}
        <div className="flex border-b border-zinc-700 overflow-x-auto scrollbar-hide">
          <button onClick={() => setActiveTab("book")}
            className={`flex-shrink-0 px-3 md:px-4 py-3 text-xs md:text-sm font-medium transition-all flex items-center justify-center gap-1 md:gap-2 ${activeTab === "book" ? "text-amber-400 border-b-2 border-amber-400 bg-zinc-800/50" : "text-zinc-400 hover:text-white hover:bg-zinc-800/30"}`}
            data-testid="tab-book-consultation">
            <Calendar className="w-4 h-4" />
            <span className="hidden sm:inline">{isFr ? "Réserver" : "Book"}</span>
          </button>
          <button onClick={() => setActiveTab("join")}
            className={`flex-shrink-0 px-3 md:px-4 py-3 text-xs md:text-sm font-medium transition-all flex items-center justify-center gap-1 md:gap-2 ${activeTab === "join" ? "text-blue-400 border-b-2 border-blue-400 bg-blue-900/30" : "text-zinc-400 hover:text-white hover:bg-zinc-800/30"}`}
            data-testid="tab-join-consultation">
            <Video className="w-4 h-4" />
            <span>{isFr ? "Rejoindre Zoom" : "Join Zoom"}</span>
          </button>
          <button onClick={() => setActiveTab("book-treatment")}
            className={`flex-shrink-0 px-3 md:px-4 py-3 text-xs md:text-sm font-medium transition-all flex items-center justify-center gap-1 md:gap-2 ${activeTab === "book-treatment" ? "text-rose-400 border-b-2 border-rose-400 bg-zinc-800/50" : "text-zinc-400 hover:text-white hover:bg-zinc-800/30"}`}
            data-testid="tab-book-treatment">
            <Calendar className="w-4 h-4" />
            <span className="hidden sm:inline">{isFr ? "Traitement" : "Treatment"}</span>
          </button>
          <button onClick={() => setActiveTab("generate")}
            className={`flex-shrink-0 px-3 md:px-4 py-3 text-xs md:text-sm font-medium transition-all flex items-center justify-center gap-1 md:gap-2 ${activeTab === "generate" ? "text-emerald-400 border-b-2 border-emerald-400 bg-zinc-800/50" : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30"}`}
            data-testid="tab-generate-code">
            <Key className="w-4 h-4" />
            <span className="hidden sm:inline">{isFr ? "Créer Code" : "Create Code"}</span>
            <span className="text-[10px] bg-zinc-700 px-1.5 py-0.5 rounded hidden lg:inline">Staff</span>
          </button>
          <button onClick={() => setActiveTab("admin")}
            className={`flex-shrink-0 px-3 md:px-4 py-3 text-xs md:text-sm font-medium transition-all flex items-center justify-center gap-1 md:gap-2 ${activeTab === "admin" ? "text-red-400 border-b-2 border-red-400 bg-zinc-800/50" : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30"}`}
            data-testid="tab-admin">
            <Shield className="w-4 h-4" />
            <span className="hidden sm:inline">Admin</span>
          </button>
        </div>

        {/* QUICK JOIN BANNER - Backup for clients who have a code */}
        {activeTab !== "join" && (
          <div className="mx-4 mt-4 mb-2">
            <button
              onClick={() => setActiveTab("join")}
              className="w-full bg-zinc-800/80 hover:bg-zinc-800 border border-blue-500/30 hover:border-blue-500/50 text-white rounded-xl p-3 transition-all group"
              data-testid="quick-join-banner"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <Video className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-sm text-zinc-300">{isFr ? "Consultation Zoom" : "Zoom Consultation"}</p>
                    <p className="text-zinc-500 text-xs">{isFr ? "Cliquez le lien dans votre email pour rejoindre" : "Click the link in your email to join"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-blue-400 text-sm">
                  <span>{isFr ? "Info" : "Info"}</span>
                  <ExternalLink className="w-4 h-4" />
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">

          {/* BOOK TAB */}
          {activeTab === "book" && (
            <div className="space-y-6">
              <div className="flex items-center gap-4 p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                <img src={KARINE_IMAGE} alt="Karine Lauzon RN" className="w-20 h-20 rounded-full object-cover border-2 border-amber-400" />
                <div>
                  <h3 className="text-white font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {isFr ? "Rencontrez Karine" : "Meet Karine"}
                  </h3>
                  <p className="text-zinc-400 text-sm">
                    {isFr ? "35+ ans d'expérience • Injectrice de précision" : "35+ years experience • Precision Injectionist"}
                  </p>
                </div>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <h3 className="text-amber-400 font-medium mb-2 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  {isFr ? "Comment ça fonctionne" : "How It Works"}
                </h3>
                <ol className="text-zinc-300 text-sm space-y-2 list-decimal list-inside">
                  <li>{isFr ? "Réservez via Square Appointments ci-dessous" : "Book via Square Appointments below"}</li>
                  <li>{isFr ? "Vous recevrez un lien Zoom par email et texto" : "You'll receive a Zoom link via email and text"}</li>
                  <li>{isFr ? "À l'heure prévue, cliquez le lien — Zoom s'ouvre automatiquement!" : "At your scheduled time, click the link — Zoom opens automatically!"}</li>
                </ol>
              </div>

              <div>
                <h3 className="text-white text-lg mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {isFr ? "Choisissez un emplacement" : "Choose a location"}
                </h3>
                <div className="grid gap-3">
                  {locations.map((loc) => (
                    <button key={loc.name} onClick={() => {
                      onClose?.();
                      // Route into our own /book flow so we stay on-site
                      // and use the fresh Square catalog + availability
                      // pipeline. `type=consultation` tells the funnel
                      // to auto-select the free consultation service.
                      navigate(`/book?location=${loc.locationId}&type=consultation`);
                    }}
                      className="w-full flex items-center justify-between p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg hover:border-amber-500/50 hover:bg-zinc-800 transition-all group">
                      <span className="text-white font-medium">{loc.name}</span>
                      <div className="flex items-center gap-2 text-amber-400">
                        <span className="text-sm">{isFr ? "Réserver" : "Book"}</span>
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* JOIN TAB */}
          {activeTab === "join" && (
            <div className="space-y-6">
              {/* Hero Banner */}
              <div className="bg-gradient-to-r from-blue-600/20 via-blue-500/20 to-cyan-500/20 border-2 border-blue-500/50 rounded-xl p-6 text-center">
                <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/30">
                  <Video className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {isFr ? "Rejoindre Votre Consultation Zoom" : "Join Your Zoom Consultation"}
                </h2>
                <p className="text-zinc-300">
                  {isFr ? "Cliquez simplement sur le lien dans votre email ou texto" : "Simply click the link in your email or text message"}
                </p>
              </div>

              {/* How it works - Simple */}
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-5">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  {isFr ? "Comment rejoindre — C'est facile!" : "How to Join — It's Easy!"}
                </h3>
                <ol className="text-zinc-300 text-sm space-y-3 list-decimal list-inside">
                  <li>{isFr ? "Vérifiez votre email ou texto pour le lien Zoom" : "Check your email or text message for your Zoom link"}</li>
                  <li>{isFr ? "Cliquez le bouton \"Rejoindre Zoom\" dans le message" : "Click the \"Join Zoom Consultation\" button in the message"}</li>
                  <li>{isFr ? "Zoom s'ouvre automatiquement — aucun compte requis!" : "Zoom opens automatically — no account needed!"}</li>
                  <li>{isFr ? "Autorisez caméra et micro quand demandé — c'est tout!" : "Allow camera & mic when prompted — that's it!"}</li>
                </ol>
              </div>

              {/* Backup: Code Entry (collapsed) */}
              <details className="bg-zinc-800/30 border border-zinc-700 rounded-lg">
                <summary className="p-3 cursor-pointer text-zinc-400 hover:text-white text-sm font-medium">
                  {isFr ? "Vous avez un code de référence? (cliquez pour entrer)" : "Have a reference code? (click to enter)"}
                </summary>
                <div className="p-4 pt-2 border-t border-zinc-700">
                  <label className="block">
                    <Input 
                      type="text" 
                      value={roomCode} 
                      onChange={(e) => { setRoomCode(e.target.value.replace(/\D/g, '')); setJoinError(""); }}
                      placeholder="0000" 
                      className="bg-zinc-900 border-2 border-zinc-600 focus:border-blue-500 text-white placeholder:text-zinc-600 text-center text-3xl tracking-[0.5em] py-4 font-mono"
                      maxLength={4} 
                      inputMode="numeric" 
                      pattern="[0-9]*" 
                      data-testid="room-code-input"
                    />
                  </label>
                  {joinError && <p className="text-red-400 text-sm text-center mt-2">{joinError}</p>}
                  
                  <Button 
                    onClick={handleJoinRoom} 
                    disabled={isJoining || roomCode.length < 4}
                    className="w-full mt-3 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 text-white py-4 text-base font-bold flex items-center justify-center gap-2 rounded-xl"
                    data-testid="join-room-button"
                  >
                    {isJoining ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {isFr ? "Connexion..." : "Connecting..."}
                      </>
                    ) : (
                      <>
                        <Video className="w-5 h-5" />
                        {isFr ? "REJOINDRE ZOOM" : "JOIN ZOOM"}
                      </>
                    )}
                  </Button>
                </div>
              </details>

              {/* Help Contact */}
              <div className="bg-zinc-800/30 border border-zinc-700 rounded-lg p-4 text-center">
                <p className="text-zinc-400 text-sm">
                  {isFr ? "Besoin d'aide? Textez le support technique: " : "Need help? Text Tech Support: "}{" "}
                  <a href="sms:613-850-0805" className="text-amber-400 font-medium hover:underline">(613) 850-0805</a>
                </p>
              </div>
            </div>
          )}

          {/* BOOK TREATMENT TAB */}
          {activeTab === "book-treatment" && (
            <div className="space-y-6">
              <div className="text-center pb-2">
                <h3 className="text-white text-lg font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {isFr ? "Réserver un Traitement" : "Book a Treatment"}
                </h3>
                <p className="text-zinc-400 text-sm mt-1">
                  {isFr ? "Sélectionnez un emplacement et un type de traitement" : "Select a location and treatment type"}
                </p>
              </div>

              {/* Step 1: Select Location */}
              <div>
                <h4 className="text-zinc-300 text-sm mb-3 font-medium flex items-center gap-2">
                  <span className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center text-amber-400 text-xs font-bold">1</span>
                  {isFr ? "Choisir l'emplacement" : "Choose Location"}
                </h4>
                <div className="grid gap-2">
                  {locations.map((loc) => (
                    <button 
                      key={loc.name} 
                      onClick={() => setSelectedLocation(loc)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                        selectedLocation?.name === loc.name 
                          ? 'bg-amber-500/20 border-amber-500 text-white' 
                          : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-500'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <MapPin className="w-4 h-4 text-amber-400" />
                        <span className="font-medium">{loc.name}</span>
                      </div>
                      {selectedLocation?.name === loc.name && (
                        <CheckCircle2 className="w-5 h-5 text-amber-400" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 2: Select Treatment Category */}
              <div className={selectedLocation ? 'opacity-100' : 'opacity-50 pointer-events-none'}>
                <h4 className="text-zinc-300 text-sm mb-3 font-medium flex items-center gap-2">
                  <span className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center text-amber-400 text-xs font-bold">2</span>
                  {isFr ? "Type de traitement" : "Treatment Type"}
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {treatmentCategories.map((cat) => (
                    <button 
                      key={cat.id} 
                      onClick={() => setSelectedTreatmentCategory(cat)}
                      className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                        selectedTreatmentCategory?.id === cat.id 
                          ? 'bg-rose-500/20 border-rose-500 text-white' 
                          : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-500'
                      }`}
                    >
                      <span className="text-lg">{cat.icon}</span>
                      <span className="font-medium text-sm">{cat.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 3: Book Button */}
              <div className={selectedLocation && selectedTreatmentCategory ? 'opacity-100' : 'opacity-50 pointer-events-none'}>
                <Button 
                  onClick={() => {
                    if (!selectedLocation || !selectedTreatmentCategory) return;
                    onClose?.();
                    // Route into our own /book flow instead of an
                    // external Square URL (see `locations` array note
                    // — Jul 9 2026 fix for the broken tinyurl → Square
                    // sales page redirect).
                    const params = new URLSearchParams({
                      location: selectedLocation.locationId,
                      category: selectedTreatmentCategory.id,
                    });
                    navigate(`/book?${params.toString()}`);
                  }}
                  disabled={!selectedLocation || !selectedTreatmentCategory}
                  className="w-full bg-gradient-to-r from-amber-600 to-rose-600 hover:from-amber-500 hover:to-rose-500 text-white py-6 flex items-center justify-center gap-2"
                  data-testid="book-treatment-button"
                >
                  <Calendar className="w-5 h-5" />
                  {isFr ? "Réserver Maintenant" : "Book Now"}
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <p className="text-zinc-500 text-xs text-center mt-2">
                  {isFr ? "Ouvre notre réservation intégrée" : "Opens our on-site booking flow"}
                </p>
              </div>
            </div>
          )}

          {/* GENERATE CODE TAB */}
          {activeTab === "generate" && (
            <div className="space-y-6">
              {!genAuthed ? (
                <div className="max-w-xs mx-auto text-center space-y-4 py-4">
                  <Lock className="w-10 h-10 text-emerald-400 mx-auto" />
                  <h3 className="text-white font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {isFr ? "Accès Personnel" : "Staff Access"}
                  </h3>
                  <p className="text-zinc-500 text-sm">{isFr ? "Entrez le code admin pour générer des codes de salle" : "Enter admin passcode to generate room codes"}</p>
                  <Input type="password" value={genPasscode} onChange={(e) => setGenPasscode(e.target.value)}
                    placeholder={isFr ? "Code admin" : "Passcode"} maxLength={4}
                    className="bg-zinc-800 border-zinc-700 text-white text-center text-xl tracking-widest"
                    data-testid="generate-passcode-input"
                    onKeyDown={(e) => { if (e.key === "Enter" && genPasscode.length === 4) handleGenAuth(); }} />
                  <Button onClick={handleGenAuth} disabled={genPasscode.length !== 4}
                    className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                    data-testid="generate-passcode-submit">
                    {isFr ? "Accéder" : "Access"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="text-center">
                    <h3 className="text-white font-medium mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {isFr ? "Générer un Code de Salle" : "Generate Room Code"}
                    </h3>
                    <p className="text-zinc-500 text-sm">{isFr ? "Créez un code et envoyez-le à votre client" : "Create a code and send it to your client"}</p>
                  </div>

                  {genCode && (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 text-center" data-testid="generated-code-display">
                      <p className="text-emerald-400 text-sm mb-3">{isFr ? "Code de salle:" : "Room Code:"}</p>
                      <div className="text-5xl font-mono tracking-[0.4em] text-white mb-4">{genCode}</div>
                      <div className="flex gap-2 justify-center flex-wrap">
                        <Button onClick={copyCode} size="sm"
                          className={`${copied ? 'bg-emerald-600' : 'bg-zinc-700 hover:bg-zinc-600'} text-white`}
                          data-testid="copy-generated-code">
                          {copied ? <><Check className="w-4 h-4 mr-1.5" /> Copied</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy Code</>}
                        </Button>
                        {genHostUrl && (
                          <Button onClick={startZoomCall} size="sm"
                            className="bg-blue-600 hover:bg-blue-500 text-white"
                            data-testid="start-zoom-call-btn">
                            <Video className="w-4 h-4 mr-1.5" /> {isFr ? "Démarrer Zoom" : "Start Zoom Call"}
                          </Button>
                        )}
                      </div>
                      <p className="text-zinc-600 text-xs mt-3">{isFr ? "Le client clique sur son lien Zoom pour rejoindre" : "Client clicks their Zoom link to join"}</p>
                    </div>
                  )}

                  <Button onClick={handleGenerateCode} disabled={genLoading}
                    className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-6 flex items-center justify-center gap-2"
                    data-testid="generate-room-btn">
                    {genLoading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <><Plus className="w-5 h-5" /> {genCode ? (isFr ? "Générer un Nouveau Code" : "Generate New Code") : (isFr ? "Générer un Code" : "Generate Code")}</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ADMIN / STAFF TAB — email + password JWT login (12h session) */}
          {activeTab === "admin" && (
            <div className="max-w-sm mx-auto text-center space-y-4 py-6">
              <Shield className="w-12 h-12 text-amber-400 mx-auto" />
              <h3 className="text-white text-lg font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isFr ? "Connexion Équipe" : "Team"} <span className="italic text-amber-400">{isFr ? "Sign-in" : "Sign-in"}</span>
              </h3>
              <Input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                placeholder={isFr ? "Courriel" : "Email"}
                autoComplete="username"
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="vc-admin-email"
              />
              <Input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                placeholder={isFr ? "Mot de passe" : "Password"}
                autoComplete="current-password"
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="vc-admin-password"
                onKeyDown={async (e) => {
                  if (e.key === "Enter" && adminEmail && adminPassword) {
                    await doStaffLogin();
                  }
                }}
              />
              <Button
                onClick={async () => await doStaffLogin()}
                disabled={!adminEmail || !adminPassword || adminBusy}
                className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold py-3"
                data-testid="vc-admin-submit"
              >
                {adminBusy
                  ? (isFr ? "Connexion..." : "Signing in...")
                  : (isFr ? "Se connecter" : "Sign in")}
              </Button>
              <p className="text-zinc-600 text-[11px]">
                {isFr ? "Mot de passe oublié ? Demandez à Karine." : "Forgot password? Ask Karine."}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VirtualConsultationModal;
